import React, { useState, useEffect } from 'react';

// Enhanced Student Management for NPBC
const StudentManagement = () => {
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCampus, setFilterCampus] = useState('all');
  const [filterCourse, setFilterCourse] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [showPreRegisterStudent, setShowPreRegisterStudent] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [studentsPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState('students'); // 'students' or 'pre-register'

  // Modals for actions
  const [viewStudent, setViewStudent] = useState(null);
  const [editStudent, setEditStudent] = useState(null);
  const [deleteStudent, setDeleteStudent] = useState(null);

  // Edit form data
  const [editFormData, setEditFormData] = useState({});

  // Pre-registration form data
  const [preRegisterFormData, setPreRegisterFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    courseId: '',
    campusId: '',
    admissionNumber: ''
  });

  // Notification state
  const [notification, setNotification] = useState(null);

  // Set edit form data when editStudent changes
  useEffect(() => {
    if (editStudent) {
      setEditFormData({
        name: editStudent.name,
        email: editStudent.email,
        phoneNumber: editStudent.phoneNumber,
        courseId: editStudent.courseId,
        campusId: editStudent.campusId,
        status: editStudent.status,
      });
    }
  }, [editStudent]);

  // Notification helpers
  const showSuccess = (message) => {
    setNotification({ type: 'success', message });
    setTimeout(() => setNotification(null), 5000);
  };

  const showError = (message) => {
    setNotification({ type: 'error', message });
    setTimeout(() => setNotification(null), 5000);
  };

  // API base URL - adjust this to match your backend
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.intuitlms.online/api';

  // Fetch students from backend
  const fetchStudents = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/students`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch students: ${response.status} ${response.statusText}`);
      }

      const studentsData = await response.json();

      // Enhance student data with additional course information
      const enhancedStudents = await Promise.all(
        studentsData.map(async (student) => {
          try {
            // Fetch course details for each student
            const token = localStorage.getItem('token');
            const courseResponse = await fetch(`${API_BASE_URL}/courses/${student.courseId}`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            const courseData = courseResponse.ok ? await courseResponse.json() : null;

            return {
              ...student,
              course: courseData || { id: student.courseId, name: 'Unknown Course', description: '' },
              // Add computed/default values for enhanced UI
              status: 'active', // Default status - you may want to add this to the database
              yearOfStudy: 1, // Default year - consider adding to database
              semester: 'Current Semester',
              enrollmentDate: student.createdAt || new Date().toISOString(),
              // Financial data - consider adding these to your database
              feeBalance: 0,
              totalFees: 0,
              paidAmount: 0,
              // Academic data - consider adding these to your database
              gpa: 0.0,
              unitsEnrolled: 0,
              attendance: 0
            };
          } catch (courseError) {
            console.error('Error fetching course for student:', student.id, courseError);
            return {
              ...student,
              course: { id: student.courseId, name: 'Unknown Course', description: '' },
              status: 'active',
              yearOfStudy: 1,
              semester: 'Current Semester',
              enrollmentDate: student.createdAt || new Date().toISOString(),
              feeBalance: 0,
              totalFees: 0,
              paidAmount: 0,
              gpa: 0.0,
              unitsEnrolled: 0,
              attendance: 0
            };
          }
        })
      );

      setStudents(enhancedStudents);
    } catch (error) {
      console.error('Error fetching students:', error);
      setError('Failed to load students. Please check your connection and try again.');
      setStudents([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch courses from backend
  const fetchCourses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/courses`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const coursesData = await response.json();
        setCourses(coursesData);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  // Fetch campuses from backend
  const fetchCampuses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/campuses`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const campusesData = await response.json();
        setCampuses(campusesData);
      }
    } catch (error) {
      console.error('Error fetching campuses:', error);
    }
  };

  useEffect(() => {
    fetchStudents();
    fetchCourses();
    fetchCampuses();
  }, []);

  // Filter and search logic
  const filteredStudents = students.filter(student => {
    const matchesSearch = student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.email.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCampus = filterCampus === 'all' ||
      (student.campus && student.campus.name === filterCampus);

    const matchesCourse = filterCourse === 'all' ||
      (student.course && student.course.name.includes(filterCourse));

    return matchesSearch && matchesCampus && matchesCourse;
  });

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterCampus, filterCourse]);

  // Sort logic
  const sortedStudents = [...filteredStudents].sort((a, b) => {
    let aValue = a[sortBy];
    let bValue = b[sortBy];

    if (sortBy === 'campus') {
      aValue = a.campus.name;
      bValue = b.campus.name;
    } else if (sortBy === 'course') {
      aValue = a.course.name;
      bValue = b.course.name;
    }

    if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  // Pagination logic
  const indexOfLastStudent = currentPage * studentsPerPage;
  const indexOfFirstStudent = indexOfLastStudent - studentsPerPage;
  const currentStudents = sortedStudents.slice(indexOfFirstStudent, indexOfLastStudent);
  const totalPages = Math.ceil(sortedStudents.length / studentsPerPage);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return '#10B981';
      case 'probation': return '#F59E0B';
      case 'suspended': return '#EF4444';
      case 'graduated': return '#8B5CF6';
      default: return '#6B7280';
    }
  };

  const getStatusBackground = (status) => {
    switch (status) {
      case 'active': return '#D1FAE5';
      case 'probation': return '#FEF3C7';
      case 'suspended': return '#FEE2E2';
      case 'graduated': return '#EDE9FE';
      default: return '#F3F4F6';
    }
  };

  // Icons
  const SearchIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="11" cy="11" r="8"></circle>
      <path d="M21 21l-4.35-4.35"></path>
    </svg>
  );

  const FilterIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
    </svg>
  );

  const PlusIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="12" y1="5" x2="12" y2="19"></line>
      <line x1="5" y1="12" x2="19" y2="12"></line>
    </svg>
  );

  const DownloadIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
      <polyline points="7 10 12 15 17 10"></polyline>
      <line x1="12" y1="15" x2="12" y2="3"></line>
    </svg>
  );

  const EditIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
    </svg>
  );

  const EyeIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
      <circle cx="12" cy="12" r="3"></circle>
    </svg>
  );

  const TrashIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="3 6 5 6 21 6"></polyline>
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
    </svg>
  );

  const UserIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
      <circle cx="12" cy="7" r="4"></circle>
    </svg>
  );

  const MailIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
      <polyline points="22 6 12 13 2 6"></polyline>
    </svg>
  );

  const PhoneIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
    </svg>
  );

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#F8FAFC',
      padding: '24px',
    }}>
      {/* Notification */}
      {notification && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 2000,
          padding: '12px 16px',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '500',
          color: 'white',
          backgroundColor: notification.type === 'success' ? '#10B981' : '#EF4444',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          animation: 'slideIn 0.3s ease-out',
        }}>
          {notification.message}
        </div>
      )}

      {/* Header */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h1 style={{
              fontSize: '28px',
              fontWeight: 'bold',
              background: 'linear-gradient(45deg, #8B5CF6 30%, #06B6D4 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              margin: '0 0 8px 0',
            }}>
              Student Management
            </h1>
            <p style={{ margin: 0, color: '#6B7280', fontSize: '16px' }}>
              Manage student records, enrollments, and academic progress
            </p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: '24px', borderBottom: '1px solid #E5E7EB' }}>
          <button
            onClick={() => setActiveTab('students')}
            style={{
              padding: '12px 24px',
              backgroundColor: activeTab === 'students' ? '#8B5CF6' : 'transparent',
              color: activeTab === 'students' ? 'white' : '#6B7280',
              border: 'none',
              borderRadius: '8px 8px 0 0',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            Students ({students.length})
          </button>
          <button
            onClick={() => setActiveTab('pre-register')}
            style={{
              padding: '12px 24px',
              backgroundColor: activeTab === 'pre-register' ? '#8B5CF6' : 'transparent',
              color: activeTab === 'pre-register' ? 'white' : '#6B7280',
              border: 'none',
              borderRadius: '8px 8px 0 0',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            Pre-register Students
          </button>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div></div>

          <div style={{ display: 'flex', gap: '12px' }}>
            {/* Placeholder buttons for future functionality */}
            <button style={{
              padding: '8px 16px',
              backgroundColor: '#F3F4F6',
              border: '1px dashed #D1D5DB',
              borderRadius: '8px',
              fontSize: '14px',
              color: '#6B7280',
              cursor: 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              <DownloadIcon />
              Import Students
            </button>

            <button
              onClick={() => setShowPreRegisterStudent(true)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#10B981',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#10B981'}
            >
              <PlusIcon />
              Pre-register Student
            </button>

            <button
              onClick={() => setShowAddStudent(true)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#8B5CF6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#7C3AED'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#8B5CF6'}
            >
              <PlusIcon />
              Add Student
            </button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '24px',
        }}>
          {[
            { label: 'Total Students', value: students.length, color: '#8B5CF6' },
            { label: 'Active Students', value: students.filter(s => s.status === 'active').length, color: '#10B981' },
            { label: 'On Probation', value: students.filter(s => s.status === 'probation').length, color: '#F59E0B' },
            { label: 'Suspended', value: students.filter(s => s.status === 'suspended').length, color: '#EF4444' }
          ].map((stat, index) => (
            <div key={index} style={{
              padding: '16px',
              backgroundColor: '#F9FAFB',
              borderRadius: '12px',
              border: `2px solid ${stat.color}20`,
              textAlign: 'center',
            }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: stat.color, marginBottom: '4px' }}>
                {stat.value}
              </div>
              <div style={{ fontSize: '14px', color: '#6B7280' }}>{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Search and Filters */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto auto auto auto',
          gap: '12px',
          alignItems: 'center',
        }}>
          <div style={{ position: 'relative' }}>
            <div style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#9CA3AF',
              pointerEvents: 'none',
            }}>
              <SearchIcon />
            </div>
            <input
              type="text"
              placeholder="Search students by name, email, or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                paddingLeft: '40px',
                paddingRight: '16px',
                paddingTop: '10px',
                paddingBottom: '10px',
                backgroundColor: '#F3F4F6',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                transition: 'all 0.2s',
                width: '100%',
              }}
              onFocus={(e) => {
                e.target.style.backgroundColor = 'white';
                e.target.style.borderColor = '#8B5CF6';
                e.target.style.boxShadow = '0 0 0 3px rgba(139, 92, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.backgroundColor = '#F3F4F6';
                e.target.style.borderColor = '#E5E7EB';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          <select
            value={filterCampus}
            onChange={(e) => setFilterCampus(e.target.value)}
            style={{
              padding: '10px 12px',
              backgroundColor: '#F9FAFB',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              cursor: 'pointer',
              minWidth: '140px',
            }}
          >
            <option value="all">All Campuses</option>
            {campuses.map(campus => (
              <option key={campus.id} value={campus.name}>{campus.name}</option>
            ))}
          </select>

          <select
            value={filterCourse}
            onChange={(e) => setFilterCourse(e.target.value)}
            style={{
              padding: '10px 12px',
              backgroundColor: '#F9FAFB',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              cursor: 'pointer',
              minWidth: '140px',
            }}
          >
            <option value="all">All Courses</option>
            {courses.map(course => (
              <option key={course.id} value={course.name}>{course.name}</option>
            ))}
          </select>

          <button style={{
            padding: '10px 16px',
            backgroundColor: '#F3F4F6',
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            fontSize: '14px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'background-color 0.2s',
          }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#E5E7EB'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#F3F4F6'}
          >
            <FilterIcon />
            Export
          </button>
        </div>
      </div>

      {/* Students Table */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      }}>
        <div style={{
          padding: '20px',
          borderBottom: '1px solid #F3F4F6',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div>
            <h3 style={{ margin: '0 0 4px 0', fontSize: '18px', fontWeight: '600', color: '#111827' }}>
              Student Records ({filteredStudents.length})
            </h3>
            <p style={{ margin: 0, fontSize: '14px', color: '#6B7280' }}>
              Showing {indexOfFirstStudent + 1}-{Math.min(indexOfLastStudent, filteredStudents.length)} of {filteredStudents.length} students
            </p>
          </div>

          {/* Placeholder for bulk actions */}
          <div style={{
            padding: '8px 16px',
            backgroundColor: '#F3F4F6',
            border: '1px dashed #D1D5DB',
            borderRadius: '8px',
            fontSize: '12px',
            color: '#6B7280',
            opacity: 0.6,
          }}>
            Bulk Actions (Coming Soon)
          </div>
        </div>

        {isLoading ? (
          <div style={{
            padding: '60px',
            textAlign: 'center',
            color: '#6B7280',
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #F3F4F6',
              borderTop: '4px solid #8B5CF6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px',
            }} />
            Loading students...
          </div>
        ) : currentStudents.length === 0 ? (
          <div style={{
            padding: '60px',
            textAlign: 'center',
            color: '#6B7280',
          }}>
            <UserIcon />
            <p style={{ margin: '16px 0 0 0', fontSize: '16px' }}>No students found</p>
            <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>Try adjusting your search or filters</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ backgroundColor: '#F9FAFB' }}>
                <tr>
                  <th style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6B7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #E5E7EB',
                  }}>
                    Student Details
                  </th>
                  <th style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6B7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #E5E7EB',
                  }}>
                    Program & Status
                  </th>
                  <th style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6B7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #E5E7EB',
                  }}>
                    Academic Info
                  </th>
                  <th style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6B7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #E5E7EB',
                  }}>
                    Financial Status
                  </th>
                  <th style={{
                    padding: '12px 16px',
                    textAlign: 'center',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6B7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #E5E7EB',
                  }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentStudents.map((student, index) => (
                  <tr
                    key={student.id}
                    style={{
                      borderBottom: '1px solid #F3F4F6',
                      transition: 'background-color 0.2s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    {/* Student Details */}
                    <td style={{ padding: '16px', minWidth: '280px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '12px',
                          background: `linear-gradient(45deg, ${getStatusColor(student.status)}, ${getStatusColor(student.status)}80)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '16px',
                        }}>
                          {student.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                        </div>
                        <div>
                          <div style={{ fontWeight: '600', fontSize: '14px', color: '#111827', marginBottom: '2px' }}>
                            {student.name}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#6B7280', marginBottom: '2px' }}>
                            <MailIcon />
                            {student.email}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#6B7280' }}>
                            <PhoneIcon />
                            {student.phoneNumber}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Program & Status */}
                    <td style={{ padding: '16px', minWidth: '200px' }}>
                      <div style={{ marginBottom: '8px' }}>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '2px' }}>
                          {student.course.name}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>
                          Year {student.yearOfStudy} • {student.semester}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6B7280' }}>
                          {student.campus.name}
                        </div>
                      </div>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '500',
                        textTransform: 'capitalize',
                        backgroundColor: getStatusBackground(student.status),
                        color: getStatusColor(student.status),
                      }}>
                        {student.status}
                      </span>
                    </td>

                    {/* Academic Info */}
                    <td style={{ padding: '16px', minWidth: '150px' }}>
                      <div style={{ marginBottom: '4px' }}>
                        <span style={{ fontSize: '12px', color: '#6B7280' }}>GPA: </span>
                        <span style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: student.gpa >= 3.5 ? '#10B981' : student.gpa >= 2.5 ? '#F59E0B' : '#EF4444'
                        }}>
                          {student.gpa}/4.0
                        </span>
                      </div>
                      <div style={{ marginBottom: '4px' }}>
                        <span style={{ fontSize: '12px', color: '#6B7280' }}>Units: </span>
                        <span style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                          {student.unitsEnrolled}
                        </span>
                      </div>
                      <div>
                        <span style={{ fontSize: '12px', color: '#6B7280' }}>Attendance: </span>
                        <span style={{
                          fontSize: '14px',
                          fontWeight: '500',
                          color: student.attendance >= 90 ? '#10B981' : student.attendance >= 75 ? '#F59E0B' : '#EF4444'
                        }}>
                          {student.attendance}%
                        </span>
                      </div>
                    </td>

                    {/* Financial Status */}
                    <td style={{ padding: '16px', minWidth: '150px' }}>
                      <div style={{ marginBottom: '4px' }}>
                        <span style={{ fontSize: '12px', color: '#6B7280' }}>Total: </span>
                        <span style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                          KSH {student.totalFees.toLocaleString()}
                        </span>
                      </div>
                      <div style={{ marginBottom: '4px' }}>
                        <span style={{ fontSize: '12px', color: '#6B7280' }}>Paid: </span>
                        <span style={{ fontSize: '14px', fontWeight: '500', color: '#10B981' }}>
                          KSH {student.paidAmount.toLocaleString()}
                        </span>
                      </div>
                      <div>
                        <span style={{ fontSize: '12px', color: '#6B7280' }}>Balance: </span>
                        <span style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: student.feeBalance === 0 ? '#10B981' : '#EF4444'
                        }}>
                          KSH {student.feeBalance.toLocaleString()}
                        </span>
                      </div>
                    </td>

                    {/* Actions */}
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        {/* View Student */}
                        <button
                          style={{
                            padding: '6px',
                            backgroundColor: '#F3F4F6',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s',
                            color: '#6B7280',
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#E5E7EB'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#F3F4F6'}
                          title="View Details"
                          onClick={() => setViewStudent(student)}
                        >
                          <EyeIcon />
                        </button>

                        {/* Edit Student */}
                        <button
                          style={{
                            padding: '6px',
                            backgroundColor: '#F3F4F6',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s',
                            color: '#6B7280',
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#E5E7EB'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#F3F4F6'}
                          title="Edit Student"
                          onClick={() => setEditStudent(student)}
                        >
                          <EditIcon />
                        </button>

                        {/* Delete Student */}
                        <button
                          style={{
                            padding: '6px',
                            backgroundColor: '#F3F4F6',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s',
                            color: '#EF4444',
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#FEE2E2'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#F3F4F6'}
                          title="Delete Student"
                          onClick={() => setDeleteStudent(student)}
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{
            padding: '20px',
            borderTop: '1px solid #F3F4F6',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <div style={{ fontSize: '14px', color: '#6B7280' }}>
              Showing {indexOfFirstStudent + 1}-{Math.min(indexOfLastStudent, filteredStudents.length)} of {filteredStudents.length} students
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {/* First Page Button */}
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                style={{
                  padding: '8px 12px',
                  backgroundColor: currentPage === 1 ? '#F3F4F6' : 'white',
                  border: '1px solid #E5E7EB',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  color: currentPage === 1 ? '#9CA3AF' : '#374151',
                  display: totalPages > 5 ? 'block' : 'none',
                }}
                title="First Page"
              >
                «
              </button>

              {/* Previous Button */}
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                style={{
                  padding: '8px 12px',
                  backgroundColor: currentPage === 1 ? '#F3F4F6' : 'white',
                  border: '1px solid #E5E7EB',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  color: currentPage === 1 ? '#9CA3AF' : '#374151',
                }}
                title="Previous Page"
              >
                ‹
              </button>

              {/* Page Numbers */}
              <div style={{ display: 'flex', gap: '4px' }}>
                {(() => {
                  const pages = [];
                  const maxVisiblePages = 5;
                  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

                  // Adjust start page if we're near the end
                  if (endPage - startPage + 1 < maxVisiblePages) {
                    startPage = Math.max(1, endPage - maxVisiblePages + 1);
                  }

                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        style={{
                          padding: '8px 12px',
                          backgroundColor: currentPage === i ? '#8B5CF6' : 'white',
                          border: '1px solid #E5E7EB',
                          borderRadius: '6px',
                          fontSize: '14px',
                          cursor: 'pointer',
                          color: currentPage === i ? 'white' : '#374151',
                          fontWeight: currentPage === i ? '600' : '400',
                          minWidth: '40px',
                        }}
                      >
                        {i}
                      </button>
                    );
                  }
                  return pages;
                })()}
              </div>

              {/* Next Button */}
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                style={{
                  padding: '8px 12px',
                  backgroundColor: currentPage === totalPages ? '#F3F4F6' : 'white',
                  border: '1px solid #E5E7EB',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  color: currentPage === totalPages ? '#9CA3AF' : '#374151',
                }}
                title="Next Page"
              >
                ›
              </button>

              {/* Last Page Button */}
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                style={{
                  padding: '8px 12px',
                  backgroundColor: currentPage === totalPages ? '#F3F4F6' : 'white',
                  border: '1px solid #E5E7EB',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  color: currentPage === totalPages ? '#9CA3AF' : '#374151',
                  display: totalPages > 5 ? 'block' : 'none',
                }}
                title="Last Page"
              >
                »
              </button>
            </div>
          </div>
        )}
      </div>

      {/* View Student Modal */}
      {viewStudent && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1060,
        }}
          onClick={() => setViewStudent(null)}
        >
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80%',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: '0', fontSize: '24px', fontWeight: 'bold', color: '#111827' }}>
                Student Details
              </h2>
              <button
                onClick={() => setViewStudent(null)}
                style={{
                  padding: '8px',
                  backgroundColor: '#F3F4F6',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  color: '#6B7280',
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '16px',
                background: `linear-gradient(45deg, ${getStatusColor(viewStudent.status)}, ${getStatusColor(viewStudent.status)}80)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '24px',
              }}>
                {viewStudent.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
              </div>
              <div>
                <h3 style={{ margin: '0 0 4px 0', fontSize: '20px', fontWeight: '600', color: '#111827' }}>
                  {viewStudent.name}
                </h3>
                <p style={{ margin: '0', color: '#6B7280', fontSize: '14px' }}>
                  Student ID: {viewStudent.id}
                </p>
                <span style={{
                  padding: '4px 8px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '500',
                  textTransform: 'capitalize',
                  backgroundColor: getStatusBackground(viewStudent.status),
                  color: getStatusColor(viewStudent.status),
                }}>
                  {viewStudent.status}
                </span>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
              <div>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                  Personal Information
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <MailIcon />
                    <span style={{ fontSize: '14px', color: '#6B7280' }}>{viewStudent.email}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <PhoneIcon />
                    <span style={{ fontSize: '14px', color: '#6B7280' }}>{viewStudent.phoneNumber}</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                  Academic Information
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div>
                    <span style={{ fontSize: '12px', color: '#6B7280' }}>Course: </span>
                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>{viewStudent.course.name}</span>
                  </div>
                  <div>
                    <span style={{ fontSize: '12px', color: '#6B7280' }}>Campus: </span>
                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>{viewStudent.campus.name}</span>
                  </div>
                  <div>
                    <span style={{ fontSize: '12px', color: '#6B7280' }}>Year: </span>
                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>{viewStudent.yearOfStudy}</span>
                  </div>
                  <div>
                    <span style={{ fontSize: '12px', color: '#6B7280' }}>Semester: </span>
                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>{viewStudent.semester}</span>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
              <div>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                  Academic Performance
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div>
                    <span style={{ fontSize: '12px', color: '#6B7280' }}>GPA: </span>
                    <span style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: viewStudent.gpa >= 3.5 ? '#10B981' : viewStudent.gpa >= 2.5 ? '#F59E0B' : '#EF4444'
                    }}>
                      {viewStudent.gpa}/4.0
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: '12px', color: '#6B7280' }}>Units Enrolled: </span>
                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>{viewStudent.unitsEnrolled}</span>
                  </div>
                  <div>
                    <span style={{ fontSize: '12px', color: '#6B7280' }}>Attendance: </span>
                    <span style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      color: viewStudent.attendance >= 90 ? '#10B981' : viewStudent.attendance >= 75 ? '#F59E0B' : '#EF4444'
                    }}>
                      {viewStudent.attendance}%
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                  Financial Information
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div>
                    <span style={{ fontSize: '12px', color: '#6B7280' }}>Total Fees: </span>
                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                      KSH {viewStudent.totalFees.toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: '12px', color: '#6B7280' }}>Amount Paid: </span>
                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#10B981' }}>
                      KSH {viewStudent.paidAmount.toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: '12px', color: '#6B7280' }}>Balance: </span>
                    <span style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: viewStudent.feeBalance === 0 ? '#10B981' : '#EF4444'
                    }}>
                      KSH {viewStudent.feeBalance.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setViewStudent(null)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#F3F4F6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
              >
                Close
              </button>
              <button
                onClick={() => {
                  setEditStudent(viewStudent);
                  setViewStudent(null);
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#8B5CF6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
              >
                Edit Student
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Student Modal */}
      {editStudent && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1060,
        }}
          onClick={() => setEditStudent(null)}
        >
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80%',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: '0', fontSize: '24px', fontWeight: 'bold', color: '#111827' }}>
                Edit Student
              </h2>
              <button
                onClick={() => setEditStudent(null)}
                style={{
                  padding: '8px',
                  backgroundColor: '#F3F4F6',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  color: '#6B7280',
                }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={async (e) => {
              e.preventDefault();
              try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${API_BASE_URL}/students/${editStudent.id}`, {
                  method: 'PUT',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                  },
                  body: JSON.stringify(editFormData),
                });

                if (response.ok) {
                  showSuccess('Student updated successfully');
                  fetchStudents(); // Refresh the list
                  setEditStudent(null);
                } else {
                  const errorData = await response.json();
                  showError(errorData.message || 'Failed to update student');
                }
              } catch (error) {
                console.error('Error updating student:', error);
                showError('An error occurred while updating the student');
              }
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={editFormData.name}
                    onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                    }}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                    Email
                  </label>
                  <input
                    type="email"
                    value={editFormData.email}
                    onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                    }}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={editFormData.phoneNumber}
                    onChange={(e) => setEditFormData({ ...editFormData, phoneNumber: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                    }}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                    Course
                  </label>
                  <select
                    value={editFormData.courseId}
                    onChange={(e) => setEditFormData({ ...editFormData, courseId: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                    }}
                    required
                  >
                    {courses.map(course => (
                      <option key={course.id} value={course.id}>{course.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                    Campus
                  </label>
                  <select
                    value={editFormData.campusId}
                    onChange={(e) => setEditFormData({ ...editFormData, campusId: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                    }}
                    required
                  >
                    {campuses.map(campus => (
                      <option key={campus.id} value={campus.id}>{campus.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                    Status
                  </label>
                  <select
                    value={editFormData.status}
                    onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                    }}
                    required
                  >
                    <option value="active">Active</option>
                    <option value="probation">Probation</option>
                    <option value="suspended">Suspended</option>
                    <option value="graduated">Graduated</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setEditStudent(null)}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#F3F4F6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#8B5CF6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                  }}
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Student Modal */}
      {deleteStudent && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1060,
        }}
          onClick={() => setDeleteStudent(null)}
        >
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                backgroundColor: '#FEE2E2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
              }}>
                <TrashIcon />
              </div>
              <h2 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: 'bold', color: '#111827' }}>
                Delete Student
              </h2>
              <p style={{ margin: '0', color: '#6B7280', fontSize: '14px' }}>
                Are you sure you want to delete <strong>{deleteStudent.name}</strong>? This action cannot be undone.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setDeleteStudent(null)}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#F3F4F6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    const response = await fetch(`${API_BASE_URL}/students/${deleteStudent.id}`, {
                      method: 'DELETE',
                      headers: {
                        'Authorization': `Bearer ${token}`
                      }
                    });

                    if (response.ok) {
                      showSuccess('Student deleted successfully');
                      fetchStudents(); // Refresh the list
                      setDeleteStudent(null);
                    } else {
                      const errorData = await response.json();
                      showError(errorData.message || 'Failed to delete student');
                    }
                  } catch (error) {
                    console.error('Error deleting student:', error);
                    showError('An error occurred while deleting the student');
                  }
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#EF4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
              >
                Delete Student
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pre-register Student Modal */}
      {showPreRegisterStudent && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1060,
        }}
          onClick={() => setShowPreRegisterStudent(false)}
        >
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80%',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: '0', fontSize: '24px', fontWeight: 'bold', color: '#111827' }}>
                Pre-register Student
              </h2>
              <button
                onClick={() => setShowPreRegisterStudent(false)}
                style={{
                  padding: '8px',
                  backgroundColor: '#F3F4F6',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  color: '#6B7280',
                }}
              >
                ✕
              </button>
            </div>

            <p style={{ margin: '0 0 24px 0', color: '#6B7280', fontSize: '14px' }}>
              Pre-register a student with their details and generate an admission number. The student can then use this admission number to complete their registration.
            </p>

            <form onSubmit={async (e) => {
              e.preventDefault();

              // Use manual admission number
              const admissionNumber = preRegisterFormData.admissionNumber;

              if (!admissionNumber) {
                showError('Please enter an admission number');
                return;
              }

              const formData = {
                ...preRegisterFormData,
                admissionNumber
              };

              try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${API_BASE_URL}/students/pre-register`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                  },
                  body: JSON.stringify(formData),
                });

                if (response.ok) {
                  const result = await response.json();
                  showSuccess(`Student pre-registered successfully! Admission Number: ${admissionNumber}`);
                  setPreRegisterFormData({
                    name: '',
                    email: '',
                    phoneNumber: '',
                    courseId: '',
                    campusId: '',
                    admissionNumber: ''
                  });
                  setShowPreRegisterStudent(false);
                  fetchStudents(); // Refresh the list
                } else {
                  const errorData = await response.json();
                  showError(errorData.message || 'Failed to pre-register student');
                }
              } catch (error) {
                console.error('Error pre-registering student:', error);
                showError('An error occurred while pre-registering the student');
              }
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={preRegisterFormData.name}
                    onChange={(e) => setPreRegisterFormData({ ...preRegisterFormData, name: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                    }}
                    placeholder="Enter student's full name"
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={preRegisterFormData.email}
                    onChange={(e) => setPreRegisterFormData({ ...preRegisterFormData, email: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                    }}
                    placeholder="Enter student's email"
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    value={preRegisterFormData.phoneNumber}
                    onChange={(e) => setPreRegisterFormData({ ...preRegisterFormData, phoneNumber: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                    }}
                    placeholder="Enter phone number"
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                    Course *
                  </label>
                  <select
                    value={preRegisterFormData.courseId}
                    onChange={(e) => setPreRegisterFormData({ ...preRegisterFormData, courseId: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                    }}
                    required
                  >
                    <option value="">Select a course</option>
                    {courses.map(course => (
                      <option key={course.id} value={course.id}>{course.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                    Campus *
                  </label>
                  <select
                    value={preRegisterFormData.campusId}
                    onChange={(e) => setPreRegisterFormData({ ...preRegisterFormData, campusId: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                    }}
                    required
                  >
                    <option value="">Select a campus</option>
                    {campuses.map(campus => (
                      <option key={campus.id} value={campus.id}>{campus.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                    Admission Number
                  </label>
                  <input
                    type="text"
                    value={preRegisterFormData.admissionNumber}
                    onChange={(e) => setPreRegisterFormData({ ...preRegisterFormData, admissionNumber: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                    }}
                    placeholder="Enter admission number"
                    required
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setShowPreRegisterStudent(false)}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#F3F4F6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#8B5CF6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                  }}
                >
                  Pre-register Student
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Placeholder for Add Student Modal */}
      {showAddStudent && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1060,
        }}
          onClick={() => setShowAddStudent(false)}
        >
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80%',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 16px 0', fontSize: '24px', fontWeight: 'bold', color: '#111827' }}>
              Add New Student
            </h2>
            <p style={{ margin: '0 0 24px 0', color: '#6B7280' }}>
              This feature is coming soon. The student registration form will include comprehensive fields for academic and personal information.
            </p>
            <div style={{
              padding: '20px',
              backgroundColor: '#F9FAFB',
              borderRadius: '12px',
              border: '2px dashed #E5E7EB',
              textAlign: 'center',
              marginBottom: '24px',
            }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>📝</div>
              <div style={{ fontSize: '16px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                Student Registration Form
              </div>
              <div style={{ fontSize: '14px', color: '#6B7280' }}>
                Will include fields for personal info, academic program, fees, guardian details, and more
              </div>
            </div>
            <button
              onClick={() => setShowAddStudent(false)}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#8B5CF6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '500',
                cursor: 'pointer',
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes slideIn {
          0% { transform: translateX(100%); opacity: 0; }
          100% { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default StudentManagement;
